import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import CarRateDialog from '@/components/admin/property/CarRateDialog';
import { formatCurrency, formatDate } from '@/utils/format';

export default function CarRatesTab({
  carTypes,
  cars,
  selectedCar,
  openCarRateManagement,
  carRates,
  showCarRateDialog,
  setShowCarRateDialog,
  editingCarRate,
  setEditingCarRate,
  carRateForm,
  setCarRateForm,
  resetCarRateForm,
  handleCreateCarRate,
  handleUpdateCarRate,
  openEditCarRate,
  handleDeleteCarRate
}) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Car Rate Management</h2>
        <p className="text-sm text-gray-600">Set special pricing for specific date ranges by car type</p>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Car Types Selection */}
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-semibold">Select Car Type</h3>
          </div>
          
          {/* Car Types Section */}
          <div>
            <div className="space-y-2">
              {carTypes.map((type) => (
                <div
                  key={type.id}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedCar === `type-${type.id}` 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  onClick={() => openCarRateManagement(`type-${type.id}`)}
                  data-testid={`select-car-type-${type.name}`}
                >
                  <div className="font-semibold text-sm">{type.name}</div>
                  <div className="text-xs text-gray-500">
                    All {type.name} cars ({cars.filter(c => c.car_type_id === type.id).length} cars)
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rates */}
        <div className="lg:col-span-2">
          {selectedCar ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Rates</CardTitle>
                  <CarRateDialog
                    showCarRateDialog={showCarRateDialog}
                    setShowCarRateDialog={setShowCarRateDialog}
                    editingCarRate={editingCarRate}
                    setEditingCarRate={setEditingCarRate}
                    carRateForm={carRateForm}
                    setCarRateForm={setCarRateForm}
                    resetCarRateForm={resetCarRateForm}
                    handleCreateCarRate={handleCreateCarRate}
                    handleUpdateCarRate={handleUpdateCarRate}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {carRates.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No rate overrides set. Click "Add Rate" to create one.</p>
                ) : (
                  <div className="space-y-2">
                    {carRates.map((rate) => (
                      <div key={rate.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <div className="font-medium">{formatCurrency(rate.price)}/day</div>
                          <div className="text-sm text-gray-600">
                            {formatDate(rate.start_date)} - {formatDate(rate.end_date)}
                          </div>
                          {rate.car_type_id && (
                            <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 mt-1">
                              Car Type Rate
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditCarRate(rate)} data-testid={`edit-car-rate-${rate.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCarRate(rate.id)} data-testid={`delete-car-rate-${rate.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">Select a car type to manage its rates</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
